import { useState, useCallback, useMemo } from 'react'
import { SlidersHorizontal, ChevronDown, Calendar, Users, Plus, Plane } from '@/components/icons'
import { hapticLight } from '@/lib/haptics'
import { Button } from '../ui/button'
import { useNavigate, useSearch } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { TopBarBackIconButton } from '../topbar'
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs'
import { RosterEntryDrawer } from '../RosterEntryDrawer'
import { RosterStatusDrawer, getRosterDefaults } from '../RosterStatusDrawer'
import { useRosterGetHistory } from '../../lib/hooks/roster'
import { SingleRosterEntryDrawer } from '../SingleRosterEntryDrawer'
import { RosterFilterDrawer, type RosterFilters } from '../RosterFilterDrawer'
import { AircraftSelectionDrawer } from '../AircraftSelectionDrawer'
import { RosterGanttView, type FocusedEntryInfo, type SelectedRosterEntry, type EmptyCellInfo } from '../RosterGanttView'
import { RosterListView } from './RosterListView'
import {
  RosterCalendarView,
  type RosterEntryWithCrew,
} from '../RosterCalendarView'
import { AdminCrewRosterCalendarDrawer, type RosterEntryWithCrew as AdminRosterEntry } from '../AdminCrewRosterCalendarDrawer'
import { EmptyState } from '../EmptyState'
import { formatDutyStatus } from '../../utils/statusColors'
import { useCrewGetAll } from '../../lib/hooks/crew'
import { useRosterGetAll, useRosterHasAny } from '../../lib/hooks/roster'
import { useAircraftGetAll } from '../../lib/hooks'
import { X } from '@/components/icons'
import { DutyStatus } from '../../../../api/src/shared'
import { cn, getInitials } from '@/lib/utils'
import { Card } from '../ui/card'
import { Avatar, AvatarFallback } from '../ui/avatar'
import { AppLayout } from '../AppLayout'
import { DesktopPageHeader } from '../desktop/DesktopPageHeader'
import { useIsDesktop } from '../ui/use-mobile'
import { Link } from '@tanstack/react-router'
import { RosterKpiCardSkeleton, RosterContentSkeleton, RosterDutyStatusKey } from '../RosterKpiCard'
import { RosterDateNav } from '../RosterDateNav'

export type TabValue = 'calendar' | 'list' | 'gantt'

interface RosterSearchParams {
  tab?: TabValue
  selectedMonth?: string
  filterCrewIds?: string[]
  filterDutyStatuses?: string[]
  filterAircraftIds?: string[]
}

export function CrewRoster() {
  const navigate = useNavigate()
  const isDesktop = useIsDesktop()
  const search = useSearch({ strict: false }) as RosterSearchParams

  // Get active tab from URL, default to 'gantt'
  const activeTab: TabValue = search.tab && ['calendar', 'list', 'gantt'].includes(search.tab)
    ? search.tab
    : 'gantt'

  // Initialize filters from URL params
  const [filters, setFilters] = useState<RosterFilters>(() => {
    const crewMemberIds = search.filterCrewIds ?? []
    const dutyStatuses = (search.filterDutyStatuses ?? []) as DutyStatus[]
    const aircraftIds = search.filterAircraftIds ?? []
    const hasAnyFilter = crewMemberIds.length > 0 || dutyStatuses.length > 0 || aircraftIds.length > 0
    return {
      crewMemberIds,
      dutyStatuses,
      aircraftIds,
      showAll: !hasAnyFilter,
    }
  })

  // Sync filters to URL when they change
  const handleFiltersChange = useCallback((newFilters: RosterFilters) => {
    setFilters(newFilters)
    navigate({
      search: {
        ...search,
        filterCrewIds: newFilters.crewMemberIds.length > 0 ? newFilters.crewMemberIds : undefined,
        filterDutyStatuses: newFilters.dutyStatuses.length > 0 ? newFilters.dutyStatuses : undefined,
        filterAircraftIds: newFilters.aircraftIds.length > 0 ? newFilters.aircraftIds : undefined,
      } as any,
      replace: true,
    })
  }, [navigate, search])
  const [showAddDrawer, setShowAddDrawer] = useState(false)
  const [showFilterDrawer, setShowFilterDrawer] = useState(false)
  const [showAircraftDrawer, setShowAircraftDrawer] = useState(false)
  // State for single roster entry editing (gantt/list view)
  const [selectedEntry, setSelectedEntry] = useState<SelectedRosterEntry | null>(null)
  const [showSingleEntryDrawer, setShowSingleEntryDrawer] = useState(false)
  // State for creating new entry from empty cell
  const [emptyCell, setEmptyCell] = useState<EmptyCellInfo | null>(null)
  const [showNewEntryDrawer, setShowNewEntryDrawer] = useState(false)
  const [selectedCrewIdForAdd, setSelectedCrewIdForAdd] = useState<string | null>(null)
  const { data: rosterHistoryForAdd } = useRosterGetHistory(selectedCrewIdForAdd)
  // State for scheduled crew drawer (calendar view)
  const [showScheduledCrewDrawer, setShowScheduledCrewDrawer] = useState(false)
  const [selectedDateForDrawer, setSelectedDateForDrawer] = useState<Date | null>(null)
  const [selectedEntriesForDrawer, setSelectedEntriesForDrawer] = useState<RosterEntryWithCrew[]>([])
  // State for roster status drawer (crew avatar click)
  const [showRosterStatusDrawer, setShowRosterStatusDrawer] = useState(false)
  const [selectedCrewIdForStatus, setSelectedCrewIdForStatus] = useState<string | null>(null)
  // State for focusing on a newly created/updated entry
  const [focusedEntry, setFocusedEntry] = useState<FocusedEntryInfo | null>(null)

  // Visible month range, scoped to keep the roster fetch and summary lightweight.
  // Formatted to match the calendar/list views' query keys so the fetch dedupes.
  const summaryMonth = search.selectedMonth
    ? dayjs(search.selectedMonth, 'YYYY-MM')
    : dayjs()
  const monthStart = summaryMonth.startOf('month').format('YYYY-MM-DD')
  const monthEnd = summaryMonth.endOf('month').format('YYYY-MM-DD')

  // Fetch crew, roster entries (visible month only), and aircraft.
  // Crew excluded from the schedule never appear on the roster.
  const { data: rawCrewMembers = [], isLoading: isLoadingCrew } = useCrewGetAll()
  const crewMembers = useMemo(
    () => rawCrewMembers.filter((c) => c.includeInSchedule !== false),
    [rawCrewMembers]
  )
  const { data: rosterEntries = [] } = useRosterGetAll({
    startDate: monthStart,
    endDate: monthEnd,
    aircraftIds: filters.aircraftIds.length > 0 ? filters.aircraftIds : undefined,
  })
  const { data: rosterHasAny = false, isLoading: isLoadingHasAny } = useRosterHasAny()
  const { data: allAircraft = [], isLoading: isLoadingAircraft } = useAircraftGetAll()
  // Gate the initial render only on month-independent data. `isLoadingRoster`
  // re-triggers on every month switch (its query key includes selectedMonth);
  // including it here would unmount the whole calendar each time. Per-month roster
  // loading is handled inside the active view instead.
  const isInitialLoading = isLoadingCrew || isLoadingHasAny || isLoadingAircraft
  const hasNoCrew = crewMembers.length === 0
  const hasNoRoster = !rosterHasAny

  // Summary counts for the KPI card, scoped to the visible month
  const summary = useMemo(() => {
    const monthStartDay = summaryMonth.startOf('month')
    const monthEndDay = summaryMonth.endOf('month')

    const onDuty = new Set<string>()
    const offDuty = new Set<string>()
    const training = new Set<string>()
    const standby = new Set<string>()
    const leave = new Set<string>()
    const sick = new Set<string>()
    const positioning = new Set<string>()

    rosterEntries.forEach((entry: any) => {
      const entryDate = dayjs(entry.date)
      if (entryDate.isBefore(monthStartDay, 'day') || entryDate.isAfter(monthEndDay, 'day')) {
        return
      }
      if (filters.crewMemberIds.length > 0 && !filters.crewMemberIds.includes(entry.crewId)) return
      if (filters.dutyStatuses.length > 0 && !filters.dutyStatuses.includes(entry.dutyStatus)) return

      switch (entry.dutyStatus) {
        case DutyStatus.OnDuty: onDuty.add(entry.crewId); break
        case DutyStatus.OffDuty: offDuty.add(entry.crewId); break
        case DutyStatus.Training: training.add(entry.crewId); break
        case DutyStatus.Standby: standby.add(entry.crewId); break
        case DutyStatus.Leave: leave.add(entry.crewId); break
        case DutyStatus.Sick: sick.add(entry.crewId); break
        case DutyStatus.Positioning: positioning.add(entry.crewId); break
      }
    })

    return {
      onDuty: onDuty.size,
      offDuty: offDuty.size,
      training: training.size,
      standby: standby.size,
      leave: leave.size,
      sick: sick.size,
      positioning: positioning.size,
    }
  }, [rosterEntries, search.selectedMonth, filters.crewMemberIds, filters.dutyStatuses])

  // Get selected filter display values
  const selectedCrewMembers = filters.crewMemberIds
    .map(id => crewMembers.find(c => c.id === id))
    .filter((c): c is NonNullable<typeof c> => !!c)
  // Aircraft selection is single-or-none: [] = All Aircraft, [id] = one aircraft.
  const selectedAircraftId = filters.aircraftIds[0] ?? null
  const selectedAircraft = selectedAircraftId
    ? allAircraft.find(a => a.id === selectedAircraftId) ?? null
    : null
  const hasSummaryData = Object.values(summary).some((count) => count > 0)

  // Solid status-dot color for the aircraft filter pill, mirroring the
  // AircraftStatusBadge variants (Active=green, Maintenance=amber, else red).
  const aircraftStatusDotColor = (status: string) =>
    status === 'Active'
      ? 'bg-background-positive-bold'
      : status === 'Maintenance'
        ? 'bg-background-notice-bold'
        : 'bg-background-negative-bold'

  // Shared date navigation (prev/next or the month picker) writes the month to
  // the URL; all three views derive their month from `selectedMonth`.
  const goToMonth = (month: dayjs.Dayjs) => {
    navigate({
      search: { ...search, selectedMonth: month.format('YYYY-MM') } as any,
      replace: true,
    })
  }

  const handleAircraftSelect = (aircraftId: string | null) => {
    handleFiltersChange({
      ...filters,
      aircraftIds: aircraftId ? [aircraftId] : [],
      showAll: !aircraftId && filters.crewMemberIds.length === 0 && filters.dutyStatuses.length === 0,
    })
  }

  const handleTabChange = (value: string) => {
    const tab = value as TabValue
    // Update URL with new tab parameter, preserving other search params
    navigate({
      search: { ...search, tab } as any,
      replace: true,
    })
  }

  const handleCalendarDayClick = (date: Date, entries: RosterEntryWithCrew[]) => {
    // Show scheduled crew drawer for calendar view
    setSelectedDateForDrawer(date)
    setSelectedEntriesForDrawer(entries)
    setShowScheduledCrewDrawer(true)
  }

  const setActiveTab = (tab: TabValue) => {
    navigate({
      search: { ...search, tab } as any,
      replace: true,
    })
  }

  return (
    <AppLayout
      footer={
        !isInitialLoading && !hasNoCrew && !hasNoRoster && hasSummaryData ? (
          <div className="px-gutter pb-2">
            <RosterDutyStatusKey summary={summary} />
          </div>
        ) : undefined
      }
      topBar={
        isDesktop ? (
          <div className="px-gutter pt-8 pb-2">
            <DesktopPageHeader
              title="Schedule"
              subtitle="Control rosters and more…"
              breadcrumb={
                <span>
                  <Link to="/operations" className="hover:underline">
                    Operations
                  </Link>
                  <span className="text-content-primary-subtle"> / Schedule</span>
                </span>
              }
              action={
                <div className="flex items-center gap-3">
                  <Button
                    variant="secondary"
                    size="sm"
                    roundness="round"
                    onClick={() => setShowFilterDrawer(true)}
                  >
                    <SlidersHorizontal />
                  </Button>
                  {!hasNoCrew && (
                    <Button
                      variant="brand"
                      size="sm"
                      roundness="round"
                      className="gap-2"
                      onClick={() => {
                        hapticLight()
                        setSelectedCrewIdForAdd(null)
                        setShowAddDrawer(true)
                      }}
                    >
                      Add Roster
                      <Plus className="size-4 text-content-primary-white" />
                    </Button>
                  )}
                </div>
              }
            />
          </div>
        ) : (
        <TopBarBackIconButton
          title="Schedule"
          rightElement={
            <div className="flex items-center gap-3">
              {/* Filter button — matches the default TopBarBackIconButton icon button */}
              <Button
                variant="secondary"
                size="sm"
                roundness="round"
                onClick={() => setShowFilterDrawer(true)}
              >
                <SlidersHorizontal />
              </Button>
              {/* Create-schedule brand button */}
              {!hasNoCrew && (
                <button
                  onClick={() => {
                    hapticLight()
                    setSelectedCrewIdForAdd(null)
                    setShowAddDrawer(true)
                  }}
                  className="bg-background-brand-bold flex items-center justify-center rounded-full size-8 hover:bg-background-brand-bold/90 transition-colors p-[7px]"
                >
                  <Plus className="w-[14.75px] h-[14.75px] text-white" />
                </button>
              )}
            </div>
          }
        />
        )
      }
      wrapperClassName={activeTab === 'gantt' ? 'overflow-hidden' : undefined}
      className={cn(
        activeTab === 'gantt' && 'flex flex-col overflow-hidden',
      )}
    >
      {/* Loading state */}
      {isInitialLoading ? (
        <div className="flex flex-col gap-4 py-4 flex-1">
          <RosterKpiCardSkeleton />
          <RosterContentSkeleton />
        </div>
      ) : hasNoCrew ? (
        <div>
          <EmptyState
            icon={Users}
            title="No crew available"
            description="Add crew members before creating roster entries."
            addButtonText="Add Crew Member"
            onAdd={() => navigate({ to: '/operations/crew-directory', search: { openDrawer: true } })}
          />
        </div>
      ) : hasNoRoster ? (
        <div>
          <Card className="bg-surface-L2 border-border-L2 p-8">
            <div className="flex flex-col items-center gap-4">
              <div className="flex items-center justify-center rounded-lg bg-background-brand-secondary-subtle p-2">
                <Calendar className="size-4 text-content-brand-bold" />
              </div>
              <div className="flex flex-col gap-1 text-center">
                <p className="text-base font-semibold text-content-primary-bold tracking-tight">
                  Nothing scheduled yet
                </p>
                <p className="text-sm text-content-primary-subtle">
                  To schedule crew members choose a crew member from below.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 justify-center">
                {crewMembers.map((crew) => (
                  <button
                    key={crew.id}
                    onClick={() => {
                      setSelectedCrewIdForAdd(crew.id)
                      setShowAddDrawer(true)
                    }}
                    className="cursor-pointer"
                  >
                    <Avatar className="size-10">
                      <AvatarFallback
                        style={{ backgroundColor: crew.avatarColor || undefined }}
                        className="text-xs font-medium text-background"
                      >
                        {getInitials(crew.firstName, crew.lastName)}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                ))}
              </div>
            </div>
          </Card>
        </div>
      ) : (
      <>
      {/* Filter row: compact aircraft pill + active filter chips inline */}
      <div className="flex items-center gap-2 flex-wrap pb-2">
        <button
          onClick={() => {
            hapticLight()
            setShowAircraftDrawer(true)
          }}
          className="inline-flex items-center gap-2 bg-surface-L1 border border-border-L1 rounded-2xl pl-3 pr-2 py-1 active:bg-surface-L2 transition-colors"
        >
          <Plane className="size-5 text-content-brand-bold shrink-0" />
          <span className="text-sm font-semibold text-content-primary-bold whitespace-nowrap">
            {selectedAircraft ? selectedAircraft.registration : 'All Aircraft'}
          </span>
          {selectedAircraft && (
            <span
              className={cn(
                'size-2.5 rounded-full shrink-0',
                aircraftStatusDotColor(selectedAircraft.aircraftStatus)
              )}
            />
          )}
          <ChevronDown className="size-5 text-content-primary-bold shrink-0" />
        </button>

        {selectedCrewMembers.map((crew) => (
          <button
            key={crew.id}
            onClick={() =>
              handleFiltersChange({
                ...filters,
                crewMemberIds: filters.crewMemberIds.filter(id => id !== crew.id),
              })
            }
            className="inline-flex items-center gap-1.5 px-2 py-[3px] rounded-full bg-background-primary-bold text-content-primary-inverse text-xs font-medium leading-normal tracking-[0.18px]"
          >
            {crew.firstName && crew.lastName
              ? `${crew.firstName} ${crew.lastName}`
              : crew.name}
            <X className="size-3" />
          </button>
        ))}
        {filters.dutyStatuses.map((status) => (
          <button
            key={status}
            onClick={() =>
              handleFiltersChange({
                ...filters,
                dutyStatuses: filters.dutyStatuses.filter(s => s !== status),
              })
            }
            className="inline-flex items-center gap-1.5 px-2 py-[3px] rounded-full bg-background-primary-bold text-content-primary-inverse text-xs font-medium leading-normal tracking-[0.18px]"
          >
            {formatDutyStatus(status)}
            <X className="size-3" />
          </button>
        ))}
      </div>

      {/* Date navigation — drives selectedMonth for all three views */}
      <RosterDateNav
        month={summaryMonth}
        onMonthChange={goToMonth}
        className="pb-2"
      />

      {/* Tabs - visible only when there are roster entries */}
      <div className="pb-2">
        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className="w-full"
        >
          <TabsList className='w-full'>
            <TabsTrigger value="gantt">
              Plan
            </TabsTrigger>
            <TabsTrigger value="calendar">
              Calendar
            </TabsTrigger>
            <TabsTrigger value="list">
              List
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Content area */}
      {activeTab === 'gantt' ? (
        /* Gantt fills remaining space, scrolls internally */
        <RosterGanttView
          className="flex-1 min-h-0"
          onEntryClick={(entry) => {
            setSelectedEntry(entry)
            setShowSingleEntryDrawer(true)
          }}
          onEmptyCellClick={(info) => {
            setEmptyCell(info)
            setShowNewEntryDrawer(true)
          }}
          onCrewAvatarClick={(crewId) => {
            setSelectedCrewIdForStatus(crewId)
            setShowRosterStatusDrawer(true)
          }}
          focusedEntry={focusedEntry}
          onFocusComplete={() => setFocusedEntry(null)}
          filters={filters}
        />
      ) : (
        <div className="flex flex-col gap-4 relative w-full z-10 flex-1 min-h-0">
          {/* Calendar View */}
          {activeTab === 'calendar' && (
            <RosterCalendarView
              onDayClick={handleCalendarDayClick}
              filters={filters}
            />
          )}

          {/* List View */}
          {activeTab === 'list' && (
            <RosterListView
              onEntryClick={(entry) => {
                setSelectedEntry(entry)
                setShowSingleEntryDrawer(true)
              }}
              filters={filters}
              className="flex-1 min-h-0"
            />
          )}
        </div>
      )}

      </>
      )}

      {/* Drawers */}
      {(() => {
        const defaults = getRosterDefaults(rosterHistoryForAdd?.[0] ?? null)
        return (
          <RosterEntryDrawer
            open={showAddDrawer}
            onOpenChange={(open) => {
              setShowAddDrawer(open)
              if (!open) {
                setSelectedCrewIdForAdd(null)
              }
            }}
            defaultCrewId={selectedCrewIdForAdd}
            defaultAircraftId={selectedAircraft?.id ?? null}
            defaultStartDate={defaults?.startDate}
            defaultEndDate={defaults?.endDate}
            defaultDutyStatus={defaults?.dutyStatus}
            defaultPatternDaysOn={defaults?.patternDaysOn}
            defaultPatternDaysOff={defaults?.patternDaysOff}
            onSuccess={(info) => {
              setActiveTab('gantt')
              setFocusedEntry(info)
            }}
            onRemove={() => {
              setSelectedCrewIdForStatus(null)
            }}
          />
        )
      })()}

      {/* Keep the entry/cell set on close so the drawer stays mounted through
          its exit animation; the next tap overwrites it. */}
      {selectedEntry && (
        <SingleRosterEntryDrawer
          open={showSingleEntryDrawer}
          onOpenChange={setShowSingleEntryDrawer}
          rosterId={selectedEntry.entryId}
          onSuccess={() => {
            setShowSingleEntryDrawer(false)
          }}
        />
      )}

      {emptyCell && (
        <SingleRosterEntryDrawer
          open={showNewEntryDrawer}
          onOpenChange={setShowNewEntryDrawer}
          crewId={emptyCell.crewId}
          date={emptyCell.date}
          crew={emptyCell.crew}
          onSuccess={() => {
            setShowNewEntryDrawer(false)
          }}
        />
      )}

      <RosterFilterDrawer
        open={showFilterDrawer}
        onOpenChange={setShowFilterDrawer}
        filters={filters}
        onFiltersChange={handleFiltersChange}
      />

      <AircraftSelectionDrawer
        open={showAircraftDrawer}
        onOpenChange={setShowAircraftDrawer}
        selectedAircraftId={selectedAircraftId}
        onSelect={handleAircraftSelect}
      />

      <AdminCrewRosterCalendarDrawer
        isOpen={showScheduledCrewDrawer}
        onOpenChange={setShowScheduledCrewDrawer}
        date={selectedDateForDrawer}
        entries={selectedEntriesForDrawer as AdminRosterEntry[]}
        aircraftIds={filters.aircraftIds}
        onEntryClick={(entry) => {
          setShowScheduledCrewDrawer(false)
          setSelectedEntry({
            entryId: entry.id,
            crewId: entry.crewId,
            date: entry.date.toDate(),
            dutyStatus: entry.dutyStatus,
            crew: entry.crew ? {
              id: entry.crew.id,
              name: entry.crew.name,
              firstName: entry.crew.firstName,
              lastName: entry.crew.lastName,
              avatarColor: entry.crew.avatarColor,
            } : undefined,
          })
          setShowSingleEntryDrawer(true)
        }}
      />

      {/* Roster Status Drawer (crew avatar click). The crew id is kept on
          close so the drawer stays mounted through its exit animation; the
          next tap overwrites it. */}
      {selectedCrewIdForStatus && (() => {
        const crew = crewMembers.find(c => c.id === selectedCrewIdForStatus)
        if (!crew) return null
        return (
          <RosterStatusDrawer
            open={showRosterStatusDrawer}
            onOpenChange={setShowRosterStatusDrawer}
            crew={crew}
            defaultAircraftId={selectedAircraft?.id ?? null}
          />
        )
      })()}
    </AppLayout>
  )
}
